import * as ko from "knockout";
import hasher from "hasher";
import {IFormTemplate, IMessageData, IRouteDefinitionParams} from "../../../../index";
import {KOSubscriptionTracker} from "../../../../util/KOSubscriptionTracker";
import {createErrorMessage} from "../../../../util/misc";
import {nsRoute} from "../../../../util/route";
import {PLEASE_WAIT_MESSAGE} from "../../../../constants/Language";
import {getHash} from "../../../../constants/RouteDefinitions";
import {nsFormTemplate} from "../nsFormTemplate";
import {global} from "../../../../appContext";
import {EHubManagerOptions} from "../../../../../ehub/EHubManager";
import PromiseRequest from "../../../../req/PromiseRequest";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import navigateBackFromEhub = nsFormTemplate.navigateBackFromEhub;
import {isFormTemplateV2, parseRouteParamVersion} from "../routeVersion";
import versionredirectHtml from './versionredirect.html?raw';

export const template = versionredirectHtml;

export class viewModel extends ParsedRouteDataDecorator {
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private doHistoryBack: boolean = true;
    private readonly req_getformtemplate: PromiseRequest = global.getPromiseRequest('getformtemplate');

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.init();
    }

    private init(): Promise<void> {
        const version = parseRouteParamVersion(this.__routeParams?.['version']);
        if (version !== null) {
            return this.resolveVersion(version);
        }
        this.isReady(false);
        this.messageData({message: PLEASE_WAIT_MESSAGE});
        return this.req_getformtemplate
            .request({id: this.__routeParams?.['id']})
            .then((data: [IFormTemplate]) => {
                const d = data?.[0]
                if (!d) {
                    return;
                }
                const isV2Form = d.v2form
                return this.resolveVersion(isV2Form ? 2 : 1)
            })
            .then(() => {
                this.messageData(null)
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
            });
    }

    private async resolveVersion(version: number): Promise<void> {
        if (!isFormTemplateV2(version)) {
            const hash = getHash('formtemplatedetails', {id: this.__id}, false)
            hasher.replaceHash(hash)
            return
        }

        this.messageData({message: PLEASE_WAIT_MESSAGE})
        try {
            // init() is idempotent; login may preload the script, but direct navigation here must ensure it.
            await global.eHubManager.init()
            this.messageData(null)
            const manager = global.eHubManager
            global.router.onHashChange = (newHash, oldHash) => {
                return manager.eHubManager.requestClose()
                    .then((canProceed: boolean) => {
                        if (canProceed) {
                            this.doHistoryBack = false
                            manager.stop()
                            return newHash || 'ehub'
                        } else {
                            return oldHash || 'ehub'
                        }
                    })
            }

            const fnOnClose = () => {
                delete global.router.onHashChange;
                global.cacheManager.removeCache('getehubprocess')
                global.cacheManager.removeCache('getformtemplate')
                global.cacheManager.removeCache('searchformtemplate')
                if (this.doHistoryBack) {
                    navigateBackFromEhub(this.__routeParams)
                }
            }

            const options: Partial<EHubManagerOptions> = {
                form: this.__routeParams?.['id'],
                mode: this.__routeParams?.['mode'],
            }
            if (this.__routeParams?.['targetid']) {
                options.targetId = this.__routeParams['targetid']
            }

            // if id == 0 then we're in create mode - no need to pass any id parameter
            if (Number(this.__routeParams?.['id']) === 0) {
                delete options.form
            }
            await manager.start(options, {
                cancel: fnOnClose,
                close: fnOnClose,
                delete: () => {
                    navigateBackFromEhub(this.__routeParams)
                }
            })
        } catch (reason: unknown) {
            this.messageData(createErrorMessage(String(reason)))
        }
    }

    override dispose() {
        super.dispose();
        this.kosub.unsuscribeAll();
        this.req_getformtemplate.abort();
    }
}
